import type { ErrorReporter } from '@estimating/error-reporting';
import { FirebaseError, initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
  onIdTokenChanged,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth';
import type { FirebaseRuntimeConfig } from '../../app/runtime-config';
import { AuthError, type AuthErrorCode, type AuthUser, type Authenticator } from './authenticator';

/**
 * Firebase failures a person can cause or fix. Wrong email and wrong password get
 * the same code, so the page never reveals whether an account exists; projects
 * with email enumeration protection already answer `auth/invalid-credential` for both.
 */
const expectedErrors: Readonly<Record<string, AuthErrorCode>> = {
  'auth/invalid-credential': 'auth.invalid_credentials',
  'auth/invalid-login-credentials': 'auth.invalid_credentials',
  'auth/wrong-password': 'auth.invalid_credentials',
  'auth/user-not-found': 'auth.invalid_credentials',
  'auth/invalid-email': 'auth.invalid_credentials',
  'auth/user-disabled': 'auth.user_disabled',
  'auth/too-many-requests': 'auth.too_many_attempts',
  'auth/network-request-failed': 'auth.network',
  // Renewing the ID token failed for good; Firebase signs the user out as well.
  'auth/user-token-expired': 'auth.session_expired',
  'auth/invalid-user-token': 'auth.session_expired',
};

/** A blank name counts as none, so the header falls back to the email. */
function nameOrNull(name: string | null): string | null {
  const trimmed = name?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

function sameProfile(snapshot: AuthUser, user: User): boolean {
  return (
    snapshot.uid === user.uid &&
    snapshot.email === user.email &&
    snapshot.displayName === nameOrNull(user.displayName)
  );
}

/** Our code for a Firebase failure; `auth.unexpected` for anything not in the list above. */
export function toAuthErrorCode(error: unknown): AuthErrorCode {
  return error instanceof FirebaseError
    ? (expectedErrors[error.code] ?? 'auth.unexpected')
    : 'auth.unexpected';
}

/**
 * Signs in through the brand's Firebase project, which the brand's shop management
 * system shares (D-036, D-060). The session is kept across visits, as Firebase does
 * by default.
 */
export function createFirebaseAuthenticator(
  config: FirebaseRuntimeConfig,
  errorReporter: ErrorReporter,
): Authenticator {
  const auth = initializeAuth(initializeApp(config), {
    // `getAuth` would also bundle the popup and redirect code, which nothing here uses.
    persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
  });

  // Firebase changes its User object in place when the profile changes, so React
  // gets a copy instead, replaced only when something it shows has changed.
  let snapshot: AuthUser | null = null;
  function currentUser(): AuthUser | null {
    const user = auth.currentUser;
    if (user === null) {
      snapshot = null;
    } else if (snapshot === null || !sameProfile(snapshot, user)) {
      snapshot = { uid: user.uid, email: user.email, displayName: nameOrNull(user.displayName) };
    }
    return snapshot;
  }

  /** Runs a Firebase call; a failure becomes our `AuthError`, and an unexpected one is reported. */
  async function call<T>(work: () => Promise<T>): Promise<T> {
    try {
      return await work();
    } catch (error) {
      const code = toAuthErrorCode(error);
      if (code === 'auth.unexpected') {
        // Reported by Firebase's code alone: its messages and custom data can include the email.
        const firebaseCode = error instanceof FirebaseError ? error.code : 'none';
        errorReporter.captureException(new Error(`Firebase Auth failed: ${firebaseCode}`), {
          tags: { feature: 'auth', firebaseCode },
        });
      }
      throw new AuthError(code, { cause: error });
    }
  }

  return {
    ready: () => auth.authStateReady(),
    currentUser,
    // Unlike `onAuthStateChanged`, this also fires after a profile update and on each
    // hourly token refresh; `currentUser` returns the same snapshot when nothing changed.
    subscribe: (listener) =>
      onIdTokenChanged(auth, () => {
        listener();
      }),
    signInWithEmailAndPassword: async (email, password) => {
      await call(() => signInWithEmailAndPassword(auth, email, password));
    },
    signInWithCustomToken: async (customToken) => {
      await call(() => signInWithCustomToken(auth, customToken));
    },
    signOut: () => call(() => signOut(auth)),
    idToken: ({ forceRefresh }) => {
      const user = auth.currentUser;
      return user === null ? Promise.resolve(null) : call(() => user.getIdToken(forceRefresh));
    },
  };
}
